import Modal from "react-modal";
import {
  RepeatEndOption,
  RepeatRuleOption,
  RepeatRuleSettingModalProps,
  RepeatUnit,
  RepeatWeekday,
  TRRule,
} from "./types";
import {
  Container,
  Content,
  Day,
  DaysContainer,
  EndRule,
  Heading,
  Options,
  RepeatOption,
  Section,
  Title,
  Unit,
  Option,
  CancelBtn,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { useEffect, useState } from "react";
import RepeatRuleSettingModalService from "./services";
import CalendarInput from "./calendar-input";
import { RepeatReservationConditionalOption } from "../repeat-edit-conditinal-modal";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "516px",
    borderRadius: "20px",
    transform: "translate(-50%, -50%)",
    padding: 0,
    margin: 0,
    overflow: "visible",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

export default function RepeatRuleSettingModal(props: RepeatRuleSettingModalProps) {
  const { modalIsOpen, closeModal, selectedDate = new Date(), selectedRrule, editOption, minCount } = props;
  const [selectedRepeatUnit, setSelectedRepeatUnit] = useState<RepeatUnit>(RepeatUnit.WEEK);
  const [selectedRepeatWeekdays, setSelectedRepeatWeekdays] = useState<RepeatWeekday[]>([]);
  const [selectedEndOption, setSelectedEndOption] = useState<RepeatEndOption>(RepeatEndOption.DATE);
  const [selectedRuleOption, setSelectedRuleOption] = useState<RepeatRuleOption>(RepeatRuleOption.NTH_WEEKDAY);
  const [repeatInterval, setRepeatInterval] = useState<number | undefined>(1);
  const [repeatCount, setRepeatCount] = useState<number | undefined>(10);
  const after1Month = new Date();
  const after6Month = new Date();
  after1Month.setMonth(after1Month.getMonth() + 1);
  after6Month.setMonth(after6Month.getMonth() + 6);
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(after1Month);

  const services = new RepeatRuleSettingModalService();

  useEffect(() => {
    //* INFO: 선택된 rrule이 없을 때 초기화
    if (modalIsOpen === true && selectedRrule === undefined) {
      //* INFO: selectedDate값의 요일로 초기화
      setSelectedRepeatWeekdays([services.getWeekday(selectedDate)]);
      setSelectedEndOption(RepeatEndOption.DATE);
      setSelectedRuleOption(RepeatRuleOption.NTH_WEEKDAY);
      setSelectedRepeatUnit(RepeatUnit.WEEK);
      setSelectedEndDate(after1Month);
      setRepeatInterval(1);
      setRepeatCount(10);
    }
    //* INFO: 선택된 rrule이 있을 때 초기화
    else if (modalIsOpen === true && selectedRrule !== undefined) {
      setSelectedRepeatUnit(selectedRrule.repeatUnit);
      setSelectedRepeatWeekdays((selectedRrule.repeatWeekdays as RepeatWeekday[]) ?? []);
      setSelectedEndOption(selectedRrule.repeatEndOption);
      setSelectedRuleOption(selectedRrule.repeatRuleOption ?? RepeatRuleOption.NTH_WEEKDAY);
      setSelectedEndDate(selectedRrule.endDate ?? after1Month);
      setRepeatInterval(selectedRrule.interval ?? 1);
      setRepeatCount(selectedRrule.repeatCount ?? 1);

      //* INFO: 종료조건이 없을 때 (무제한) - Google에서 설정한 무제한은, 기본적으로 repeatCount를 200회, endDate를 6개월로 설정
      if (!selectedRrule.endDate && !selectedRrule.repeatCount) {
        setSelectedEndOption(RepeatEndOption.DATE);
        setSelectedEndDate(after6Month);
        setRepeatCount(200);
      }
    }
  }, [modalIsOpen]);

  const isDisabledBtn = () => {
    if (selectedEndOption === RepeatEndOption.TIMES) {
      if ((repeatCount && repeatCount > 200) || (repeatCount && minCount && repeatCount < minCount)) {
        return true;
      }
      if (!repeatCount) {
        return true;
      }
    }

    if (selectedRepeatUnit === RepeatUnit.WEEK && selectedRepeatWeekdays.length === 0) {
      return true;
    }
    return false;
  };

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelBtn>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelBtn>
        <Heading>
          <UI.Typography type="heading02" fontStyle="bold" color="var(--alias-color-label-neutral)">
            반복 조건 설정
          </UI.Typography>
        </Heading>
        <Section>
          <Content>
            <RepeatOption>
              <div style={{ display: "flex", gap: 24 }}>
                <Unit show={true}>
                  <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                    반복 단위
                  </UI.Typography>
                  <UI.SelectBox
                    data={Object.values(RepeatUnit)}
                    selectedData={[selectedRepeatUnit]}
                    onChange={(value: string[]) => {
                      setSelectedRepeatUnit(value[0] as RepeatUnit);
                      if (value[0] === "주중(월-금) 매일") {
                        setRepeatInterval(undefined);
                      }
                      if (value[0] === "주") {
                        setSelectedRepeatWeekdays([services.getWeekday(selectedDate)]);
                      } else {
                        setSelectedRepeatWeekdays([]);
                      }
                    }}
                  />
                </Unit>
                <Unit show={selectedRepeatUnit !== RepeatUnit.WEEKDAYS}>
                  <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                    주기
                  </UI.Typography>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", whiteSpace: "nowrap" }}>
                    <UI.Input
                      value={repeatInterval?.toString()}
                      onChange={(e) => {
                        const value = parseInt(e);
                        if (value > 0) {
                          setRepeatInterval(value);
                        } else setRepeatInterval(undefined);
                      }}
                    />
                    <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                      {services.getRepeatUnitLabel(selectedRepeatUnit)}
                    </UI.Typography>
                  </div>
                </Unit>
              </div>
              <DaysContainer show={selectedRepeatUnit === RepeatUnit.WEEK}>
                {Object.values(RepeatWeekday).map((day, index) => (
                  <Day
                    key={index}
                    onClick={() => {
                      if (selectedRepeatWeekdays.includes(day)) {
                        setSelectedRepeatWeekdays(selectedRepeatWeekdays.filter((selectedDay) => selectedDay !== day));
                      } else {
                        setSelectedRepeatWeekdays([...selectedRepeatWeekdays, day]);
                      }
                    }}
                  >
                    <UI.CheckBox selected={selectedRepeatWeekdays.includes(day)} />
                    <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                      {day}
                    </UI.Typography>
                  </Day>
                ))}
              </DaysContainer>
              <DaysContainer show={selectedRepeatUnit === RepeatUnit.MONTH}>
                <Option onClick={() => setSelectedRuleOption(RepeatRuleOption.NTH_WEEKDAY)}>
                  <UI.Radio selected={selectedRuleOption === RepeatRuleOption.NTH_WEEKDAY} />
                  <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                    {services.getNthWeekday(selectedDate)}
                  </UI.Typography>
                </Option>
                <Option onClick={() => setSelectedRuleOption(RepeatRuleOption.SPECIFIC)}>
                  <UI.Radio selected={selectedRuleOption === RepeatRuleOption.SPECIFIC} />
                  <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                    {selectedDate.getDate()}일
                  </UI.Typography>
                </Option>
              </DaysContainer>
            </RepeatOption>
            <RepeatOption>
              <EndRule>
                <Title>
                  <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                    종료
                  </UI.Typography>
                  <div style={{ position: "relative" }}>
                    <UI.ToolTip
                      styles={{
                        zIndex: 0,
                      }}
                      text={`예약 반복의 최대 기간은 ${editOption === RepeatReservationConditionalOption.ALL_SCHEDULES ? "당일 기준" : ""} 6개월 / 최대 횟수는 200회 입니다.`}
                      position={{ top: -31, left: -29 }}
                    >
                      <UI.Icon name="info" size={20} color="var(--alias-color-label-assistive)" />
                    </UI.ToolTip>
                  </div>
                </Title>
                <Options>
                  {Object.values(RepeatEndOption).map((option, index) => (
                    <Option onClick={() => setSelectedEndOption(option)} key={index}>
                      <UI.Radio selected={option === selectedEndOption} />
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {option}
                      </UI.Typography>
                    </Option>
                  ))}
                </Options>
              </EndRule>
              <div style={{ width: 180, display: "flex", gap: 8, alignItems: "center" }}>
                {selectedEndOption === RepeatEndOption.TIMES ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", position: "relative" }}>
                    <UI.Input
                      width={178}
                      style={{
                        whiteSpace: "nowrap",
                      }}
                      helpText={`이미 ${minCount}회 반복되었습니다. 최소 ${minCount}회 이상으로 작성해주세요.`}
                      showHelpText={minCount && repeatCount && repeatCount < minCount ? true : false}
                      error={
                        repeatCount === undefined ||
                        repeatCount > 200 ||
                        (minCount && repeatCount && repeatCount < minCount ? true : false)
                      }
                      value={repeatCount?.toString()}
                      onChange={(e) => {
                        const value = parseInt(e);
                        if (value > 0) {
                          setRepeatCount(value);
                        } else {
                          setRepeatCount(undefined);
                        }
                      }}
                    />
                    <UI.Typography
                      type="body01 normal"
                      color="var(--alias-color-label-normal)"
                      fontStyle="regular"
                      style={{
                        position: "absolute",
                        top: 12,
                        left: 186,
                      }}
                    >
                      회
                    </UI.Typography>
                  </div>
                ) : (
                  <CalendarInput value={selectedEndDate} onChange={setSelectedEndDate} />
                )}
              </div>
            </RepeatOption>
            <UI.Button
              title="설정하기"
              size="large"
              disabled={isDisabledBtn()}
              style={{ width: "100%" }}
              onClick={() => {
                if (isDisabledBtn()) return;
                const rrule: TRRule = {
                  repeatUnit: selectedRepeatUnit,
                  interval: repeatInterval ?? 1,
                  repeatEndOption: selectedEndOption,
                  repeatCount,
                  endDate: selectedEndDate,
                  repeatWeekdays: selectedRepeatWeekdays,
                  repeatRuleOption: selectedRuleOption,
                };

                props.onSetting?.(rrule);
                closeModal();
              }}
            />
          </Content>
        </Section>
      </Container>
    </Modal>
  );
}
